import { z } from 'zod';
import { IsoDateTime } from '../ingest/common.ts';

/**
 * What happened to one of our pushes on the device (BB-72 contract, handler and facts — BB-79), reported as an SDK
 * batch operation (BB-25). `received` is the SDK receipt (the only evidence of `delivered`), `opened` a direct open
 * through the body or a button, `button_clicked` any button tap (a dismissive one is not an open), `dismissed` a
 * swipe-away (Android only). A server that does not know the type answers per operation with
 * `unknown_operation_type`; the rest of the batch is unaffected. Unknown fields are ignored.
 */
export const PUSH_RECEIPT_SOURCES = ['fcm_service', 'ios_foreground', 'ios_background', 'ios_nse'] as const;
/** Why a received push was not shown. */
export const PUSH_NOT_DISPLAYED_REASONS = ['expired', 'duplicate', 'identity_changed', 'unsupported_version', 'channel_disabled', 'notifications_disabled', 'silent'] as const;
export const PUSH_APP_STATES = ['foreground', 'background', 'not_running'] as const;

/** Button ids come back as the OS reports them, so they are not held to the payload's `PushButtonId` pattern. */
const ReportedButtonId = z.string().min(1).max(64);

export const PushInteraction = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('received'),
    displayed: z.boolean(),
    source: z.enum(PUSH_RECEIPT_SOURCES),
    reason: z.enum(PUSH_NOT_DISPLAYED_REASONS).optional(),
  }),
  z.object({ kind: z.literal('opened'), via: z.enum(['body', 'button']), buttonId: ReportedButtonId.optional(), coldStart: z.boolean() }),
  z.object({ kind: z.literal('button_clicked'), buttonId: ReportedButtonId, dismissive: z.boolean() }),
  z.object({ kind: z.literal('dismissed') }),
]);
export type PushInteraction = z.infer<typeof PushInteraction>;

export const PushInteractionOperation = z
  .object({
    type: z.literal('push_interaction'),
    operationId: z.uuid(),
    occurredAt: IsoDateTime,
    sessionId: z.uuid().optional(),
    /** `bb.did` of the push. */
    deliveryId: z.uuid(),
    appState: z.enum(PUSH_APP_STATES),
    interaction: PushInteraction,
  })
  .superRefine((op, ctx) => {
    const i = op.interaction;
    if (i.kind === 'opened' && i.via === 'button' && i.buttonId === undefined) ctx.addIssue({ code: 'custom', path: ['interaction', 'buttonId'], message: 'an open through a button names the button' });
    if (i.kind === 'opened' && i.via === 'body' && i.buttonId !== undefined) ctx.addIssue({ code: 'custom', path: ['interaction', 'buttonId'], message: 'an open through the body has no button' });
    if (i.kind === 'received' && i.displayed && i.reason !== undefined) ctx.addIssue({ code: 'custom', path: ['interaction', 'reason'], message: 'a displayed push has no reason' });
    if (i.kind === 'received' && !i.displayed && i.reason === undefined) ctx.addIssue({ code: 'custom', path: ['interaction', 'reason'], message: 'a push that was not displayed says why' });
  });
export type PushInteractionOperation = z.infer<typeof PushInteractionOperation>;
